import {
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  fromDaysToBiggestAsPossible,
  toDays,
  toFragment,
  type DurationFragment,
} from '@/utils/dateTime';

import { FragmentSelector } from './fragmentSelector';
import { getHumanizeRetentionText } from './retentionSelectionPopover';

import {
  annuallyFrequency,
  dailyFrequency,
  monthlyFrequency,
  weeklyFrequency,
  type Frequency,
} from '../frequency';

const frequencyOptions: Frequency[] = [
  dailyFrequency,
  weeklyFrequency,
  monthlyFrequency,
  annuallyFrequency,
];

export function RetentionSelector({
  minimumRetentionDays,
  maximumRetentionDays,
  retentionInDays,
  onChange,
  isInvalidRetention,
  invalidRetentionError,
}: {
  minimumRetentionDays?: number;
  maximumRetentionDays?: number;
  retentionInDays?: number;
  onChange: (retentionInDays: number) => void;
  isInvalidRetention?: boolean;
  invalidRetentionError?: string;
}) {
  const formattedRetention = fromDaysToBiggestAsPossible(retentionInDays || 0);
  const [selectedFragment, setSelectedFragment] = useState<DurationFragment>(
    formattedRetention.unit
  );

  const frequency =
    frequencyOptions.find(
      ({ retentionUnit: unit }) => selectedFragment === unit
    ) || dailyFrequency;

  const [retentionByFragment, setRetentionByFragment] = useState(
    toFragment(
      selectedFragment,
      retentionInDays || frequency.defaultRetentionInDays
    )
  );

  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!isInternalChange.current && retentionInDays !== undefined) {
      const newFragment = toFragment(selectedFragment, retentionInDays);
      if (newFragment !== retentionByFragment) {
        setRetentionByFragment(newFragment);
      }
    }
    isInternalChange.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retentionInDays]);

  const onFragmentChange = (newUnit: DurationFragment) => {
    isInternalChange.current = true;
    setSelectedFragment(newUnit);
    onChange(toDays(newUnit, retentionByFragment));
  };

  const onRetentionChange = (retention: number) => {
    isInternalChange.current = true;
    setRetentionByFragment(retention);
    onChange(toDays(selectedFragment, retention));
  };

  return (
    <Stack gap='12px'>
      <Stack direction='row' alignItems='start' gap='12px'>
        <FrequencyTextField
          initialValue={retentionByFragment}
          fragment={selectedFragment}
          onChange={onRetentionChange}
          isInvalidRetention={isInvalidRetention}
          invalidRetentionError={invalidRetentionError}
          minValue={minimumRetentionDays}
          maxValue={maximumRetentionDays}
        />
        <FragmentSelector
          fragment={selectedFragment}
          onChange={onFragmentChange}
        />
      </Stack>
      {selectedFragment !== 'days' && (
        <Typography variant='body2'>{`${retentionByFragment} ${selectedFragment} will be saved as ${toDays(selectedFragment, retentionByFragment)} days.`}</Typography>
      )}
    </Stack>
  );
}

const FrequencyTextField = (props: {
  initialValue: number;
  fragment: DurationFragment;
  onChange: (retentionInDays: number) => void;
  isInvalidRetention?: boolean;
  invalidRetentionError?: string;
  minValue?: number;
  maxValue?: number;
}) => {
  const [value, setValue] = useState(`${props.initialValue}`);

  useEffect(() => {
    if (value !== `${props.initialValue}`) {
      setValue(`${props.initialValue}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValue]);

  const invalidError = useMemo(() => {
    const retentionInDays = toDays(props.fragment, +value);
    const isLessThanMin = !!(
      props.minValue && retentionInDays < props.minValue
    );
    const isGreaterThanMax = !!(
      props.maxValue && retentionInDays > props.maxValue
    );

    const invalidError =
      isLessThanMin && props.minValue
        ? `Retention time can't be shorter than ${getHumanizeRetentionText(props.minValue)}`
        : isGreaterThanMax && props.maxValue
          ? `Retention time can't be more than ${getHumanizeRetentionText(props.maxValue)}`
          : props.isInvalidRetention
            ? props.invalidRetentionError
            : '';
    return invalidError;
  }, [
    value,
    props.fragment,
    props.minValue,
    props.maxValue,
    props.isInvalidRetention,
    props.invalidRetentionError,
  ]);

  return (
    <TextField
      sx={{
        '& .MuiOutlinedInput-root': {
          paddingRight: '4px',
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
          {
            display: 'none',
          },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
      }}
      data-testid='retention-input'
      className='w-[100px]'
      type='number'
      slotProps={{
        htmlInput: { min: 1, step: 1, pattern: '[0-9]*', inputMode: 'numeric' },
        input: {
          endAdornment: (
            <Stack direction='row' gap='4px' alignItems='center'>
              {!!invalidError && (
                <InputAdornment position='start'>
                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: 'var(--mui-palette-error-main)',
                        },
                      },
                    }}
                    title={invalidError}
                  >
                    <i className='material-symbols-error-outline-rounded bg-(--mui-palette-error-main)' />
                  </Tooltip>
                </InputAdornment>
              )}
            </Stack>
          ),
        },
      }}
      variant='outlined'
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        if (e.target.value) {
          props.onChange(+e.target.value);
        }
      }}
      onBlur={() => {
        let numericValue = +value;
        numericValue = isNaN(numericValue) ? 1 : numericValue;
        numericValue = Math.max(1, numericValue);
        setValue(`${numericValue}`);
        props.onChange(numericValue);
      }}
      error={!!invalidError}
    />
  );
};
